// @ts-nocheck
// Agent Portal (/cti2) — the persistent Live-call card stack, split out of
// AgentPortalPage.tsx. Matches the mock's live-call "cti-v3-center" column
// and production's own IdleScreen.tsx pattern: CustomerProfileCard/
// CallScriptCard/DispositionCard are always mounted and decide their own
// visibility from useCallStore (isActive/hasDispContext) — the parent
// never branches on `screen` to show/hide them. Replaces the separate
// RingingScreen/DispositionScreen full-screen takeovers entirely — their
// Accept/Reject is now the Dialer-rail buttons, their ringtone audio has
// its own home in useScreenState. Call controls (mute/hold/transfer/conf/
// hang-up) already live in Dialer.tsx and stay visible regardless of
// screen, so nothing here duplicates them; Transfer/Conference already
// route to the shared TransferConferencePicker in AgentPortalPage.tsx.
// Pending dispositions are no longer a separate full-screen takeover
// (PendingDispositionsPanel) — DispositionCard's own Live/Pending tabs
// (same pattern as CallScriptCard's Script/Objections tabs) show that
// list inline, right here.
import React, { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ClipboardList } from 'lucide-react';
import { useCallStore } from '@/router/zustand/callStore';
import CustomerProfileCard from '@/features/cti/components/CustomerProfileCard';
import CallScriptCard from '@/features/cti/components/CallScriptCard';
import DispositionCard from '@/features/cti/components/DispositionCard';
import LiveCallTranscript from './LiveCallTranscript';

export default function LiveCallStack({
  hideScript = false,
  transcriptionEnabled = false,
  contextScript,
  contextScriptSource,
  contextScriptLoading,
  dispositionMeta,
  dispositionValues,
  setDispositionValues,
  dispositionErrors,
  setDispositionErrors,
  remarkStatusList,
  dispositionSubmitting,
  onSubmitDisposition,
  onSubmitDispositionAndNext,
  onSubmitDispositionAndExit,
  editingSubmitted,
  onEditSubmitted,
  onCancelEditSubmitted,
  customerHistory,
  userData,
  dispositionSecondsLeft,
  lastSubmittedDisposition,
  pendingDispositions,
  onSubmitPending,
  onSubmitPendingAndNext,
  maskNumbers,
}) {
  const callState = useCallStore((s) => s.callState);
  const onCall = Boolean(callState && callState !== 'idle');
  const pendingCount = pendingDispositions?.length || 0;
  const hasDisposition = Boolean(dispositionMeta) || pendingCount > 0;
  // Wrap-up: the call is over and its form is open — that is the agent's only job now.
  const wrapUp = Boolean(dispositionMeta) && !onCall;

  const stackRef = useRef<HTMLDivElement | null>(null);
  const dispRef = useRef<HTMLDivElement | null>(null);
  const [dispInView, setDispInView] = useState(true);
  // Which way the card lies when it is off screen, so the jump pill points at it.
  const [dispAbove, setDispAbove] = useState(false);
  const [flash, setFlash] = useState(false);

  // Track whether the disposition card is on screen inside the scrolling stack, and
  // which side it is on. Direction is measured live (on every scroll / visibility
  // change) because the card starts empty (0×0 rect) and fills in later — a rect
  // cached from that moment would wrongly read as "above".
  useEffect(() => {
    const root = stackRef.current;
    const el = dispRef.current;
    if (!root || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const measureSide = () => {
      const r = el.getBoundingClientRect();
      if (!r.height) return; // empty card — nothing to point at yet
      setDispAbove(r.bottom <= root.getBoundingClientRect().top);
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        setDispInView(entry.isIntersecting);
        measureSide();
      },
      { root, threshold: 0.15 },
    );
    io.observe(el);
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measureSide);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      io.disconnect();
      root.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll the card into view (it keeps its place in the stack) and pulse it.
  const flashTimer = useRef<any>(null);
  const jumpToDisposition = () => {
    dispRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlash(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(false), 2400);
  };
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  // Call just ended: bring the form on screen once. If the agent scrolls away afterwards,
  // the jump pill below takes over.
  useEffect(() => {
    if (wrapUp) jumpToDisposition();
  }, [wrapUp]);

  const showJump = hasDisposition && !dispInView;
  const fmtLeft = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div
      ref={stackRef}
      className="cti-live-stack flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-1"
    >
      <CustomerProfileCard showQualityIndicator />
      {transcriptionEnabled && <LiveCallTranscript agentName={userData?.name || 'Agent'} />}
      {!hideScript && (
        <CallScriptCard
          alwaysShow
          contextScript={contextScript}
          contextScriptSource={contextScriptSource}
          contextScriptLoading={contextScriptLoading}
          showObjectionsTab={false}
        />
      )}
      <div
        ref={dispRef}
        className={`cti-disp-slot${flash ? ' cti-disp-slot--flash' : ''}`}
      >
      <DispositionCard
        dispositionMeta={dispositionMeta}
        dispositionValues={dispositionValues}
        setDispositionValues={setDispositionValues}
        dispositionErrors={dispositionErrors}
        setDispositionErrors={setDispositionErrors}
        remarkStatusList={remarkStatusList}
        dispositionSubmitting={dispositionSubmitting}
        onSubmit={onSubmitDisposition}
        onSubmitAndNext={onSubmitDispositionAndNext}
        onSubmitAndExit={onSubmitDispositionAndExit}
        editingSubmitted={editingSubmitted}
        onEditSubmitted={onEditSubmitted}
        onCancelEditSubmitted={onCancelEditSubmitted}
        customerHistory={customerHistory}
        userData={userData}
        dispositionSecondsLeft={dispositionSecondsLeft}
        dispositionTotalSeconds={dispositionMeta?.wrap_up_seconds ?? null}
        lastSubmittedDisposition={lastSubmittedDisposition}
        pendingDispositions={pendingDispositions}
        onSubmitPending={onSubmitPending}
        onSubmitPendingAndNext={onSubmitPendingAndNext}
        maskNumbers={maskNumbers}
      />
      </div>

      {/* Sticky jump pill — the form sits below the profile/transcript/script during a
          call and scrolls out of sight; this keeps it one click away. */}
      {showJump && (
        <button type="button" className="cti-disp-jump" onClick={jumpToDisposition}>
          <ClipboardList size={16} />
          <span>
            {dispositionMeta
              ? 'Disposition form'
              : `${pendingCount} pending disposition${pendingCount === 1 ? '' : 's'}`}
          </span>
          {dispositionMeta && dispositionSecondsLeft != null ? (
            <span className="cti-disp-jump__timer">{fmtLeft(dispositionSecondsLeft)}</span>
          ) : null}
          <span className={`cti-disp-jump__arrow${dispAbove ? ' cti-disp-jump__arrow--up' : ''}`}>
            {dispAbove ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
          </span>
        </button>
      )}
    </div>
  );
}
